import {
  Box,
  Center,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { FaCalendarCheck, FaUserPlus, FaUsers } from "react-icons/fa";
import { RiStackFill } from "react-icons/ri";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { fetchStatsApi } from "../../api/admin";
import LayoutView from "../../components/layout";
import Auth from "../../hoc/Auth";

const Dashboard = () => {
  const navigate = useNavigate();
  const statsQuery = useQuery(["fetchStatsApi"], fetchStatsApi);
  const stats = statsQuery.data?.data;

  const statCards = [
    {
      label: "Total patients",
      value: stats?.patients?.total,
      detail: `${stats?.patients?.approved || 0} approved patients`,
      icon: FaUsers,
      color: "brand",
    },
    {
      label: "Appointments in queue",
      value: stats?.appointments?.open,
      detail: `${stats?.appointments?.closed || 0} completed`,
      icon: RiStackFill,
      color: "orange",
    },
  ];

  const quickActions = [
    { icon: FaUserPlus, title: "Register patient", description: "Create a new patient record", path: "/patient/register-patient" },
    { icon: FaUsers, title: "Patient records", description: "Find and manage patients", path: "/patients" },
    { icon: FaCalendarCheck, title: "Appointment queue", description: "Review today’s consultations", path: "/appointments" },
  ];

  return (
    <LayoutView activePage="Dashboard">
      <Box maxW="1100px" mx="auto">
        <Box>
          <Heading size="md">Today at a glance</Heading>
          <Text mt="1" color="gray.500">A summary of patient and appointment activity.</Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="5" mt="6">
          {statCards.map((card) => (
            <Box key={card.label} bg="white" borderRadius="2xl" boxShadow="card" p={{ base: "5", md: "6" }}>
              <Flex justify="space-between" align="flex-start">
                <Box>
                  <Text color="gray.500" fontWeight="600">{card.label}</Text>
                  <Skeleton isLoaded={!statsQuery.isLoading} mt="2" w="100px">
                    <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="800" color="gray.800">{card.value ?? 0}</Text>
                  </Skeleton>
                  <Text mt="3" fontSize="sm" color="gray.500">{card.detail}</Text>
                </Box>
                <Center w="48px" h="48px" borderRadius="xl" bg={`${card.color}.50`} color={`${card.color}.600`}>
                  <Icon as={card.icon} boxSize="5" />
                </Center>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>

        <Box mt={{ base: "8", md: "10" }}>
          <Heading size="md">Quick actions</Heading>
          <Text mt="1" color="gray.500">Jump into your most common workflows.</Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="5" mt="6">
            {quickActions.map((action) => (
              <Box
                key={action.title}
                as="button"
                textAlign="left"
                bg="white"
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="2xl"
                p="6"
                onClick={() => navigate(action.path)}
                _hover={{ borderColor: "brand.200", boxShadow: "card", transform: "translateY(-2px)" }}
              >
                <Center w="44px" h="44px" borderRadius="xl" bg="brand.50" color="brand.600">
                  <Icon as={action.icon} boxSize="5" />
                </Center>
                <Text mt="5" fontWeight="800" fontSize="lg">{action.title}</Text>
                <Text mt="1" color="gray.500" fontSize="sm">{action.description}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    </LayoutView>
  );
};

export default Auth(Dashboard);
